import React from 'react';
import { Typography } from '@mui/material';
import { GuideSection } from '../common/GuideSection';
import { StepByStep } from '../common/StepByStep';
import { Tip } from '../common/Tip';
import { Warning } from '../common/Warning';
import { useTranslation } from '../../../i18n';

/**
 * How a customer shares a drone's flights with XER (#1690).
 *
 * Every UI label in the copy is interpolated from the key the UI itself
 * renders (the Files tab, the Shared view, ShareDroneFlightsDialog and the
 * folder share, the .bin upload prompt and Link to flight), so a renamed button renames the guide's step with it, in
 * every locale.
 */
export const ShareFlightsSection: React.FC = () => {
  const { t } = useTranslation();

  const labels = {
    files: t('tabs.files'),
    sharedWithMe: t('sharedView.navLabel'),
    flights: t('sharedView.kindFlights'),
    inbound: t('sharedView.inbound'),
    outbound: t('sharedView.outbound'),
    unshare: t('sharedView.revoke'),
    openBulk: t('sharedView.openBulk'),
    drone: t('flightShares.drone'),
    organization: t('flightShares.selectOrganization'),
    fromDate: t('flightShares.fromDate'),
    access: t('flightShares.permission'),
    read: t('flightShares.permissionRead'),
    share: t('flightShares.bulkShare'),
    shareFolder: t('objectShares.menuItemFolder'),
    folderOrganization: t('objectShares.selectOrganization'),
    folderShare: t('objectShares.share'),
    sameFlight: t('flightLinks.promptYes'),
    linkFlight: t('flightLinks.menuItem'),
  };

  return (
    <GuideSection id="share-flights" title={t('guide.sections.shareFlights')}>
      <Typography variant="body1" sx={{ mb: 2, color: '#555' }}>
        {t('guide.shareFlights.intro')}
      </Typography>

      <StepByStep
        steps={[
          t('guide.shareFlights.steps.s1', labels),
          t('guide.shareFlights.steps.s2', labels),
          t('guide.shareFlights.steps.s3', labels),
          t('guide.shareFlights.steps.s4', labels),
        ]}
      />

      <Typography variant="body1" sx={{ mb: 2, color: '#555' }}>
        {t('guide.shareFlights.whatTheySee', labels)}
      </Typography>

      {/* #1693: drone pages show own + shared flights, each one marked */}
      <Typography variant="body1" sx={{ mb: 2, color: '#555' }}>
        {t('guide.shareFlights.whereTheyAppear', labels)}
      </Typography>

      <Warning>{t('guide.shareFlights.warningAdmins', labels)}</Warning>
      <Tip>{t('guide.shareFlights.tipReshare', labels)}</Tip>
      <Tip>{t('guide.shareFlights.tipHandoverDate', labels)}</Tip>
      <Tip>{t('guide.shareFlights.tipBinLogs', labels)}</Tip>
    </GuideSection>
  );
};
